/**
 * Zustand store para gestionar notificaciones
 * Migrado desde NotificationContext para mejor rendimiento y simplicidad
 * Adaptado para AdminConsole (JavaScript)
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";

const initialState = {
  notifications: [],
  unreadCount: 0,
  settings: {
    enablePushNotifications: true,
    enableSoundNotifications: true,
    enableDesktopNotifications: true,
    autoMarkAsRead: false,
    maxNotifications: 50,
  },
};

// Helper function to play notification sound
const playNotificationSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
    oscillator.frequency.setValueAtTime(600, audioContext.currentTime + 0.1);

    gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.2);
  } catch (error) {
    console.error("Error playing notification sound:", error);
  }
};

// Helper function to show desktop notification
const showDesktopNotification = (title, message, id) => {
  if ("Notification" in window && Notification.permission === "granted") {
    try {
      new Notification(title, {
        body: message,
        icon: "/favicon.ico",
        tag: id,
      });
    } catch (error) {
      console.error("Error showing desktop notification:", error);
    }
  }
};

export const useNotificationStore = create(
  persist(
    (set, get) => ({
      ...initialState,

      addNotification: (notification) => {
        const state = get();
        const newNotification = {
          ...notification,
          id: `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          timestamp: new Date(),
          read: false,
        };

        // Update state
        set((state) => {
          const updatedNotifications = [newNotification, ...state.notifications].slice(
            0,
            state.settings.maxNotifications
          );

          return {
            notifications: updatedNotifications,
            unreadCount: updatedNotifications.filter((n) => !n.read).length,
          };
        });

        // Show toast notification if enabled (simplified - sin dependencia de toast)
        if (state.settings.enablePushNotifications) {
          // Aquí podrías integrar con un sistema de toast si lo tienes
          console.log("Notification:", notification.title, notification.message);
        }

        // Play sound if enabled
        if (state.settings.enableSoundNotifications) {
          playNotificationSound();
        }

        // Show desktop notification if enabled
        if (state.settings.enableDesktopNotifications) {
          showDesktopNotification(notification.title, notification.message, newNotification.id);
        }
      },

      markAsRead: (notificationId) =>
        set((state) => {
          const updatedNotifications = state.notifications.map((notification) =>
            notification.id === notificationId ? { ...notification, read: true } : notification
          );

          return {
            notifications: updatedNotifications,
            unreadCount: updatedNotifications.filter((n) => !n.read).length,
          };
        }),

      markAllAsRead: () =>
        set((state) => ({
          notifications: state.notifications.map((notification) => ({
            ...notification,
            read: true,
          })),
          unreadCount: 0,
        })),

      deleteNotification: (notificationId) =>
        set((state) => {
          const updatedNotifications = state.notifications.filter(
            (notification) => notification.id !== notificationId
          );

          return {
            notifications: updatedNotifications,
            unreadCount: updatedNotifications.filter((n) => !n.read).length,
          };
        }),

      clearAllNotifications: () =>
        set(() => ({
          notifications: [],
          unreadCount: 0,
        })),

      updateSettings: (newSettings) =>
        set((state) => ({
          settings: { ...state.settings, ...newSettings },
        })),
    }),
    {
      name: "acv-notifications",
      partialize: (state) => ({
        notifications: state.notifications.map((n) => ({
          ...n,
          timestamp: n.timestamp.toISOString(),
        })),
        settings: state.settings,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Restore Date objects after rehydration
          state.notifications = state.notifications.map((n) => ({
            ...n,
            timestamp: new Date(n.timestamp),
          }));
          state.unreadCount = state.notifications.filter((n) => !n.read).length;
        }
      },
    }
  )
);

export default useNotificationStore;

